import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, ArrowRightLeft, Hash, ShieldCheck, Cpu, Zap, Phone, Sparkles, Server } from 'lucide-react';
import * as Icons from 'lucide-react';
import PageHero from '../components/PageHero';
import PricingCard from '../components/PricingCard';
import FaqAccordion from '../components/FaqAccordion';
import CtaBanner from '../components/CtaBanner';
import CrmMarquee from '../components/CrmMarquee';
import Seo from '../components/Seo';
import { usePageContent, getSectionFields } from '../hooks/usePageContent';
import { VOIP_PRICING_MONTHLY, VOIP_PRICING_ANNUAL, FAQS, STEPS_NEW_NUMBER, STEPS_TRANSFER, FEATURES } from '../data/mock';

export default function BusinessVoIP() {
  const [billing, setBilling] = useState('annual');
  const plans = billing === 'annual' ? VOIP_PRICING_ANNUAL : VOIP_PRICING_MONTHLY;
  const [numTab, setNumTab] = useState('new');

  // Admin-editable "How it works" section. Falls back to sensible defaults
  // so the page renders even on first install before the seed has run.
  const { page } = usePageContent('business-voip');
  const how = getSectionFields(page, 'how_it_works') || {};
  const howEyebrow = how.eyebrow || 'How it works';
  const howTitle = how.title || 'Modern phone systems, made simple';
  const howBody = how.body || 'VoIP converts calls into digital data packets and transmits them via the internet using SIP and RTP protocols. There is no clutter, no expensive copper line and no specialised hardware required. Configure everything from a clean online control panel.';
  const howVideo = how.video || '/assets/videos/voipinger-how-it-works.mp4';
  const howImage = how.image || '/assets/VoIP-Business-Phone-Systems.png';
  const howVideoPoster = how.video_poster || howImage;

  return (
    <>
      <Seo
        title="Cloud Hosted PBX | Business Phone System | VoIPinger"
        description="Cloud-hosted PBX and business phone system from VoIPinger. HD audio, smart routing, 99.99% uptime SLA, three Tier-4 datacentres. Try free for 14 days."
        path="/business-voip"
        image="https://voipinger.com/assets/heroes/business-voip.png"
        service={{ name: 'Cloud Hosted PBX', serviceType: 'Hosted PBX / Cloud Telephony' }}
        product={{ name: 'VoIPinger Cloud Hosted PBX', description: 'Business phone system with HD audio, smart routing, contact-centre features and 99.99% uptime.' }}
        faq={FAQS}
        breadcrumbs={[{ name: 'Home', path: '/' }, { name: 'Cloud Hosted PBX', path: '/business-voip' }]}
      />
      <PageHero
        eyebrow="Cloud Hosted PBX"
        title="Cloud Hosted PBX — Business Phone System for the modern office"
        subtitle="Replace expensive on-premises switchboards with a cloud-hosted PBX. HD business phone calls, smart routing and unlimited scalability — no contracts, no hardware lock-in."
        breadcrumb="Cloud Hosted PBX"
      />

      {/* Pricing */}
      <section className="bg-white">
        <div className="container-x section-pad">
          <div className="max-w-3xl mx-auto text-center mb-10">
            <span className="eyebrow">Best Price & Performance</span>
            <h2 className="section-title mt-4">Cloud Hosted PBX — Business Phone Packages</h2>
            <p className="section-sub mt-3">Switch any time. No contracts. 14-day free trial on every plan.</p>
            <div className="divider-svg mt-6" />
          </div>

          <div className="flex justify-center mb-10">
            <div className="inline-flex items-center bg-slate-100 rounded-full p-1">
              <button
                onClick={() => setBilling('annual')}
                className={`px-5 py-2 rounded-full text-sm font-semibold transition-all ${
                  billing === 'annual' ? 'bg-white text-slate-900 shadow' : 'text-slate-500'
                }`}
              >
                Annual <span className="ml-1 text-violet-600 text-xs">save up to 30%</span>
              </button>
              <button
                onClick={() => setBilling('monthly')}
                className={`px-5 py-2 rounded-full text-sm font-semibold transition-all ${
                  billing === 'monthly' ? 'bg-white text-slate-900 shadow' : 'text-slate-500'
                }`}
              >
                Monthly
              </button>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {plans.map((p) => (
              <PricingCard key={p.name} plan={p} />
            ))}
          </div>
        </div>
      </section>

      {/* Numbers section */}
      <section className="bg-slate-50 border-y border-slate-100">
        <div className="container-x section-pad">
          <div className="max-w-3xl mx-auto text-center mb-10">
            <span className="eyebrow">Numbers Your Way</span>
            <h2 className="section-title mt-4">Bring a number, or let us provide one</h2>
          </div>

          <div className="flex justify-center mb-8">
            <div className="inline-flex bg-white rounded-full p-1 border border-slate-200 shadow-sm">
              <button
                onClick={() => setNumTab('new')}
                className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${
                  numTab === 'new' ? 'bg-violet-500 text-white' : 'text-slate-600'
                }`}
              >
                Get a new number
              </button>
              <button
                onClick={() => setNumTab('transfer')}
                className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${
                  numTab === 'transfer' ? 'bg-violet-500 text-white' : 'text-slate-600'
                }`}
              >
                Transfer your number
              </button>
            </div>
          </div>

          {/* Numbers grid — 6/5 split so the illustration column is bigger
              than the copy column. Image fills its column edge-to-edge so
              the diagram / iPhone mockup is comfortably readable instead
              of floating inside a max-w-md box. */}
          <div className="grid lg:grid-cols-11 gap-10 items-center">
            <div className="lg:col-span-6 relative">
              {/* Subtle background panel so the illustration's own pastel
                  background blends rather than looking like a floating
                  rectangle. Pure decoration. */}
              <div className="absolute -inset-3 rounded-3xl bg-gradient-to-br from-violet-100/60 via-fuchsia-50/40 to-white -z-10" />
              <img
                src={
                  numTab === 'new'
                    ? '/assets/voipinger-get-new-number.png'
                    : '/assets/voipinger-transfer-number.png'
                }
                alt={numTab === 'new' ? 'Get a new VoIP number' : 'Transfer your number'}
                className="w-full h-auto rounded-2xl shadow-lg"
                loading="lazy"
              />
            </div>
            <div className="lg:col-span-5">
              <h3 className="text-2xl font-bold text-slate-900 mb-3">
                {numTab === 'new' ? 'Local & international numbers' : 'Keep your existing number'}
              </h3>
              <p className="text-slate-600 mb-6">
                {numTab === 'new'
                  ? 'Put your business on the map with a memorable number from the UK or 145+ countries worldwide.'
                  : 'Maintain your goodwill and brand with our simple porting service. We handle every step end-to-end.'}
              </p>
              <ul className="space-y-2 mb-7">
                {(numTab === 'new'
                  ? ['Memorable numbers available', 'UK & international inventory', 'Same-day provisioning']
                  : ['Single number transfer £30', 'Range numbers £150', 'No downtime, no risk']
                ).map((b) => (
                  <li key={b} className="flex items-start gap-2 text-slate-700">
                    <Check size={16} className="text-violet-600 mt-1" />
                    <span className="text-sm">{b}</span>
                  </li>
                ))}
              </ul>
              <div className="grid sm:grid-cols-2 gap-3">
                {(numTab === 'new' ? STEPS_NEW_NUMBER : STEPS_TRANSFER).map((s) => (
                  <div key={s.n} className="rounded-xl bg-white border border-slate-200 p-4">
                    <span className="text-violet-600 font-extrabold">{s.n}</span>
                    <p className="font-semibold text-slate-900 mt-1">{s.title}</p>
                    <p className="text-xs text-slate-500 mt-1">{s.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="bg-white">
        <div className="container-x section-pad">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <span className="eyebrow">VoIP Phone Benefits</span>
            <h2 className="section-title mt-4">Why businesses of all sizes choose VoIPinger</h2>
            <p className="section-sub mt-3">An internet connection is all you need. Make and receive calls anywhere, on any device.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((f) => {
              const Ic = Icons[f.icon] || Icons.Sparkles;
              return (
                <div key={f.title} className="card-modern">
                  <div className="w-12 h-12 rounded-xl bg-violet-50 text-violet-600 flex items-center justify-center mb-4">
                    <Ic size={20} />
                  </div>
                  <h3 className="font-bold text-slate-900 mb-2">{f.title}</h3>
                  <p className="text-sm text-slate-600">{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How it works — content + video/image are admin-editable via the
          /admin/pages/business-voip "How it works" section. */}
      <section className="bg-slate-50">
        <div className="container-x section-pad grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="eyebrow">{howEyebrow}</span>
            <h2 className="section-title mt-4">{howTitle}</h2>
            <p className="section-sub mt-4">
              {howBody}
            </p>
            <div className="mt-6 grid sm:grid-cols-2 gap-3">
              {[
                { i: Cpu, t: 'Cloud-native' },
                { i: ShieldCheck, t: 'SSL encrypted' },
                { i: Zap, t: 'Instant provisioning' },
                { i: Hash, t: '+250 integrations' }
              ].map((s) => (
                <div key={s.t} className="flex items-center gap-3 bg-white rounded-xl border border-slate-200 px-4 py-3">
                  <s.i size={18} className="text-violet-600" />
                  <span className="text-sm font-semibold text-slate-700">{s.t}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="relative">
            <div className="absolute -inset-4 rounded-3xl bg-gradient-to-br from-violet-100 to-fuchsia-50 -z-10" />
            {howVideo ? (
              <video
                src={howVideo}
                poster={howVideoPoster}
                className="rounded-2xl shadow-xl w-full"
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                aria-label={howTitle}
              />
            ) : (
              <img
                src={howImage}
                alt={howTitle}
                className="rounded-2xl shadow-xl w-full"
                loading="lazy"
              />
            )}
          </div>
        </div>
      </section>

      {/* ============================================================== */}
      {/* PLATFORM & RELIABILITY — 3 DCs / 99.99% uptime                  */}
      {/* ============================================================== */}
      <section className="relative overflow-hidden bg-gradient-to-b from-white via-violet-50/30 to-white border-y border-slate-100">
        <div className="container-x section-pad">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <span className="eyebrow"><Sparkles size={11} /> Carrier-grade platform</span>
            <h2 className="section-title mt-4">A platform that doesn't fall over</h2>
            <p className="section-sub mt-3">
              The VoIPinger Cloud PBX runs across three Tier-4 datacentres with 99.99% uptime SLA and
              active-active geo-redundancy. Carrier-grade telephony engineered for businesses of every
              size — without the carrier-grade price tag.
            </p>
            <div className="divider-svg mt-6" />
          </div>

          {/* Headline stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-5xl mx-auto mb-12">
            {[
              { v: '99.99%', l: 'Platform uptime', sub: 'Across all 3 datacentres' },
              { v: '145+', l: 'Countries covered', sub: 'Local numbers worldwide' },
              { v: '24/7', l: 'NOC monitoring', sub: 'UK-based engineers' },
              { v: '3', l: 'Tier-4 datacentres', sub: 'LON / AMS / LAS' }
            ].map((s) => (
              <div key={s.l} className="rounded-2xl bg-gradient-to-br from-violet-50 to-fuchsia-50 border border-violet-100 p-4 text-center">
                <p className="text-2xl md:text-3xl font-extrabold gradient-text">{s.v}</p>
                <p className="text-xs font-semibold text-slate-800 mt-1">{s.l}</p>
                <p className="text-[11px] text-slate-500 mt-0.5">{s.sub}</p>
              </div>
            ))}
          </div>

          {/* Datacentre cards */}
          <div className="grid md:grid-cols-3 gap-5 max-w-5xl mx-auto">
            {[
              {
                city: 'London',
                facility: 'Equinix Tier-4',
                badge: 'Primary UK',
                note: 'The same Equinix campus used by the London Stock Exchange. 10 Gb resilient interconnects.'
              },
              {
                city: 'Amsterdam',
                facility: 'Equinix Tier-4',
                badge: 'EMEA',
                note: 'Active cluster for European traffic — low-latency failover from London in seconds.'
              },
              {
                city: 'Las Vegas',
                facility: 'Tier-4',
                badge: 'Americas',
                note: 'Third active node ensuring a follow-the-sun UC platform with effectively 100% uptime.'
              }
            ].map((d) => (
              <div key={d.city} className="rounded-2xl bg-white border border-slate-200 p-6 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs font-extrabold uppercase tracking-widest text-violet-600">{d.badge}</p>
                  <Server size={16} className="text-slate-400" />
                </div>
                <h3 className="text-xl font-extrabold text-slate-900">{d.city}</h3>
                <p className="text-xs text-slate-500 mt-0.5">{d.facility}</p>
                <p className="text-sm text-slate-600 mt-3 leading-relaxed">{d.note}</p>
              </div>
            ))}
          </div>

          {/* n-Share explainer */}
          <div className="max-w-4xl mx-auto mt-12 rounded-2xl border border-violet-100 bg-violet-50/50 p-6 md:p-8">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-600 to-fuchsia-600 text-white flex items-center justify-center flex-shrink-0 shadow-lg shadow-violet-500/30">
                <Cpu size={22} />
              </div>
              <div>
                <h3 className="font-extrabold text-slate-900 text-lg mb-2">n-Share geo-redundancy</h3>
                <p className="text-sm text-slate-700 leading-relaxed">
                  All three datacentres run in active <span className="font-semibold">n+ configuration</span> —
                  every node is online and able to pass call traffic, registrations and recordings
                  simultaneously. If a node fails, traffic seamlessly fails over to the others with no
                  dropped calls and no loss of configuration. This is what 99.99% uptime actually means.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white">
        <div className="container-x section-pad">
          <div className="max-w-3xl mx-auto text-center mb-10">
            <span className="eyebrow">CRM Integrations</span>
            <h2 className="section-title mt-4">Connect to 250+ business tools</h2>
          </div>
          <CrmMarquee />
        </div>
      </section>

      <FaqAccordion items={FAQS} title="Cloud Hosted PBX — Business Phone FAQ" />
      <CtaBanner title="Try the VoIPinger Cloud PBX free for 14 days" />
    </>
  );
}
